import type { AddExecutiveDelegate, UpdateExecutiveDelegate } from '@myrewards/contracts';
import {
  SpotWorkflowReset,
  SpotWorkflowSetup,
  type ResetSpotWorkflow,
  LetterTemplateList,
  LetterTemplateResponse,
  PoolLedger,
  PoolPosition,
  PoolPositions,
  ExecutiveDelegateList,
  ExecutiveDelegateSetup,
  SectorSetup,
  SectorSetupList,
  SettingHistory,
  SettingsAccess,
  SettingView,
  PersonSettings,
  NotificationEmails,
  NotificationPreferences,
  type UpdatePersonSettings,
  SetupHistory,
  type AddDelegate,
  type AddProgram,
  type AdjustBudget,
  type DeleteProgram,
  type MoveBudget,
  type UpdateDelegate,
  type UpdateProgram,
  type UploadLetterTemplateFields,
  type WriteSetting,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// Settings owned by roles (Phase 7): every call goes through `request()`, so the session cookie,
// the CSRF header and contract validation apply to all of them.

const enc = encodeURIComponent;

export function personSettingsQuery(personId: string) {
  return queryOptions({
    queryKey: ['settings', 'person', personId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/settings/people/${enc(personId)}`, { schema: PersonSettings, signal }),
    staleTime: 0,
    retry: false,
  });
}

export function updatePersonSettings(
  personId: string,
  body: UpdatePersonSettings,
): Promise<PersonSettings> {
  return request(`/api/v1/settings/people/${enc(personId)}`, {
    method: 'PATCH',
    body,
    schema: PersonSettings,
  });
}

/** My preferences › Notifications (SET1, APP-092): my rows, with what each does now. */
export const myNotificationsQuery = queryOptions({
  queryKey: ['settings', 'my-notifications'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/me/notification-preferences', { schema: NotificationPreferences, signal }),
  staleTime: 30_000,
});

/** The same rows for someone a System Admin is helping (APP-094). */
export function personNotificationsQuery(personId: string) {
  return queryOptions({
    queryKey: ['settings', 'person', personId, 'notifications'] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/settings/people/${enc(personId)}/notification-preferences`, {
        schema: NotificationPreferences,
        signal,
      }),
    staleTime: 0,
    retry: false,
  });
}

/** System administration › Notifications (SYS5, APP-092): every notice and its switches. */
export const notificationEmailsQuery = queryOptions({
  queryKey: ['settings', 'notification-emails'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/settings/notification-emails', { schema: NotificationEmails, signal }),
  staleTime: 0,
  retry: false,
});

/**
 * Which settings this person may read and edit (QA-025, APP-054): Settings shows only the cards
 * these name, and never asks for a refused one. Roles change rarely; a few minutes is fresh.
 */
export const settingsAccessQuery = queryOptions({
  queryKey: ['settings', 'access'] as const,
  queryFn: ({ signal }) => request('/api/v1/settings', { schema: SettingsAccess, signal }),
  staleTime: 5 * 60_000,
});

/** One setting with "Last changed by…" and whether this person may change it. */
export function settingQuery(key: string, scope = 'system') {
  return queryOptions({
    queryKey: ['settings', 'one', key, scope] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/settings/${enc(key)}?scope=${enc(scope)}`, { schema: SettingView, signal }),
    staleTime: 30_000,
    // A 404 means the person may not see it: asking again won't change that.
    retry: false,
  });
}

/** Every value a setting has had, newest first (its History). */
export function settingHistoryQuery(key: string, scope = 'system') {
  return queryOptions({
    queryKey: ['settings', 'history', key, scope] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/settings/${enc(key)}/history?scope=${enc(scope)}`, {
        schema: SettingHistory,
        signal,
      }),
    staleTime: 30_000,
    retry: false,
  });
}

/** Changes a setting (audited; dated where the setting is; a reason where it needs one). */
export function writeSetting(body: WriteSetting): Promise<SettingView> {
  return request('/api/v1/settings', { method: 'PUT', body, schema: SettingView });
}

/** Each visible pool's position for a fiscal year (`GET /api/v1/budgets?fiscalYear=`). */
export function budgetsForYearQuery(fiscalYear: number) {
  return queryOptions({
    queryKey: ['budgets', 'year', fiscalYear] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/budgets?fiscalYear=${String(fiscalYear)}`, {
        schema: PoolPositions,
        signal,
      }),
    staleTime: 30_000,
  });
}

/** One pool's fiscal-year ledger: every entry, who and why. */
export function poolLedgerQuery(poolId: string, fiscalYear: number) {
  return queryOptions({
    queryKey: ['budgets', 'ledger', poolId, fiscalYear] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/budgets/${enc(poolId)}/ledger?fiscalYear=${String(fiscalYear)}`, {
        schema: PoolLedger,
        signal,
      }),
    staleTime: 30_000,
  });
}

/**
 * The fiscal-year ledger as CSV (`GET /api/v1/budgets/ledger/export`), narrowed as the screen is:
 * a plain download, audited on the server. `doneForSomeone` is the "Done for someone else" chip.
 */
export function ledgerExportHref(input: {
  fiscalYear: number;
  poolId: string | null;
  change: 'initial' | 'addition' | 'reduction' | 'move' | 'top_up' | null;
  doneForSomeone?: boolean;
}): string {
  const params = new URLSearchParams({ fiscalYear: String(input.fiscalYear) });
  if (input.poolId !== null) params.set('poolId', input.poolId);
  if (input.change !== null) params.set('change', input.change);
  if (input.doneForSomeone === true) params.set('doneForSomeone', 'true');
  return `/api/v1/budgets/ledger/export?${params.toString()}`;
}

export function adjustBudget(body: AdjustBudget): Promise<PoolPosition> {
  return request('/api/v1/budgets/adjust', { method: 'POST', body, schema: PoolPosition });
}

export function moveBudget(body: MoveBudget): Promise<PoolPosition[]> {
  return request('/api/v1/budgets/move', {
    method: 'POST',
    body,
    schema: z.array(PoolPosition),
  });
}

/** Every letter template version, retired ones too, with the merge fields they may use. */
export const letterTemplatesQuery = queryOptions({
  queryKey: ['letters', 'templates'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/letter-templates?includeRetired=true', { schema: LetterTemplateList, signal }),
  staleTime: 15_000,
});

/** Uploads a new template version (multipart). It's checked for viruses before it can be used. */
export function uploadLetterTemplate(
  fields: UploadLetterTemplateFields,
  file: File,
): Promise<LetterTemplateResponse> {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined) form.append(name, value);
  }
  // The file goes last, so the server reads the fields first.
  form.append('file', file, file.name);
  return request('/api/v1/letter-templates', {
    method: 'POST',
    body: form,
    schema: LetterTemplateResponse,
  });
}

export function activateLetterTemplate(id: string): Promise<LetterTemplateResponse> {
  return request(`/api/v1/letter-templates/${enc(id)}/activate`, {
    method: 'POST',
    schema: LetterTemplateResponse,
  });
}

export function retireLetterTemplate(id: string, reason: string): Promise<LetterTemplateResponse> {
  return request(`/api/v1/letter-templates/${enc(id)}/retire`, {
    method: 'POST',
    body: { reason },
    schema: LetterTemplateResponse,
  });
}

/** Where a template downloads from: a plain same-origin link, so the browser saves it. */
export function letterTemplateHref(id: string): string {
  return `/api/v1/letter-templates/${enc(id)}/content`;
}

/** The sectors whose setup this person may open (SL9). */
export const sectorSetupListQuery = queryOptions({
  queryKey: ['sector-setup', 'list'] as const,
  queryFn: ({ signal }) => request('/api/v1/sector-setup', { schema: SectorSetupList, signal }),
  staleTime: 60_000,
  // A 404 or 403 (Preview shows sample data) won't change by asking again.
  retry: false,
});

export function sectorSetupQuery(sectorId: string) {
  return queryOptions({
    queryKey: ['sector-setup', 'one', sectorId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/sector-setup/${enc(sectorId)}`, { schema: SectorSetup, signal }),
    staleTime: 15_000,
  });
}

const setup = (sectorId: string) => `/api/v1/sector-setup/${enc(sectorId)}`;

export function addDelegate(sectorId: string, body: AddDelegate): Promise<SectorSetup> {
  return request(`${setup(sectorId)}/delegates`, { method: 'POST', body, schema: SectorSetup });
}

export function updateDelegate(
  sectorId: string,
  personId: string,
  body: UpdateDelegate,
): Promise<SectorSetup> {
  return request(`${setup(sectorId)}/delegates/${enc(personId)}`, {
    method: 'PATCH',
    body,
    schema: SectorSetup,
  });
}

export function addProgram(sectorId: string, body: AddProgram): Promise<SectorSetup> {
  return request(`${setup(sectorId)}/programs`, { method: 'POST', body, schema: SectorSetup });
}

export function updateProgram(
  sectorId: string,
  programId: string,
  body: UpdateProgram,
): Promise<SectorSetup> {
  return request(`${setup(sectorId)}/programs/${enc(programId)}`, {
    method: 'PATCH',
    body,
    schema: SectorSetup,
  });
}

export function deleteProgram(
  sectorId: string,
  programId: string,
  body: DeleteProgram,
): Promise<SectorSetup> {
  return request(`${setup(sectorId)}/programs/${enc(programId)}/delete`, {
    method: 'POST',
    body,
    schema: SectorSetup,
  });
}

/** One delegate's or program's History drawer. */
export function setupHistoryQuery(
  sectorId: string,
  target: { kind: 'delegate'; personId: string } | { kind: 'program'; programId: string },
) {
  const path =
    target.kind === 'delegate'
      ? `${setup(sectorId)}/delegates/${enc(target.personId)}/history`
      : `${setup(sectorId)}/programs/${enc(target.programId)}/history`;
  return queryOptions({
    queryKey: ['sector-setup', 'history', sectorId, target] as const,
    queryFn: ({ signal }) => request(path, { schema: SetupHistory, signal }),
    staleTime: 10_000,
  });
}

// An Executive's delegates (APP-274).
export const executiveDelegateListQuery = queryOptions({
  queryKey: ['executive-delegates', 'list'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/executive-delegates', { schema: ExecutiveDelegateList, signal }),
  staleTime: 60_000,
  // A 404 or 403 (Preview shows sample data) won't change by asking again.
  retry: false,
});

export function executiveDelegatesQuery(executiveId: string) {
  return queryOptions({
    queryKey: ['executive-delegates', 'one', executiveId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/executive-delegates/${enc(executiveId)}`, {
        schema: ExecutiveDelegateSetup,
        signal,
      }),
    staleTime: 15_000,
  });
}

export function addExecutiveDelegate(
  executiveId: string,
  body: AddExecutiveDelegate,
): Promise<ExecutiveDelegateSetup> {
  return request(`/api/v1/executive-delegates/${enc(executiveId)}`, {
    method: 'POST',
    body,
    schema: ExecutiveDelegateSetup,
  });
}

export function updateExecutiveDelegate(
  executiveId: string,
  personId: string,
  body: UpdateExecutiveDelegate,
): Promise<ExecutiveDelegateSetup> {
  return request(`/api/v1/executive-delegates/${enc(executiveId)}/${enc(personId)}`, {
    method: 'PATCH',
    body,
    schema: ExecutiveDelegateSetup,
  });
}

export function executiveDelegateHistoryQuery(executiveId: string, personId: string) {
  return queryOptions({
    queryKey: ['executive-delegates', 'history', executiveId, personId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/executive-delegates/${enc(executiveId)}/${enc(personId)}/history`, {
        schema: SetupHistory,
        signal,
      }),
    staleTime: 0,
  });
}

export const spotWorkflowSetupQuery = queryOptions({
  queryKey: ['settings', 'spot-workflow-setup'],
  queryFn: ({ signal }) =>
    request('/api/v1/settings/spot-workflow/setup', { schema: SpotWorkflowSetup, signal }),
  staleTime: 0,
});

/** APP-222: what returning Spot Award setup to not set would change, read as its dialog opens. */
export const spotWorkflowResetQuery = queryOptions({
  queryKey: ['settings', 'spot-workflow-reset'],
  queryFn: ({ signal }) =>
    request('/api/v1/settings/spot-workflow/reset', { schema: SpotWorkflowReset, signal }),
  staleTime: 0,
  retry: false,
});

/** APP-222: returns Spot Award setup to not set (audited, with the reason). */
export function resetSpotWorkflow(body: ResetSpotWorkflow): Promise<SettingView> {
  return request('/api/v1/settings/spot-workflow/reset', {
    method: 'POST',
    body,
    schema: SettingView,
  });
}
